import { Sentiment } from '../../../../entities/post/models';
import type { BrandWithThemes, ProductWithThemes, VolumeType } from '../types';
import { ThemesButton } from '../ui/theme.button';

import type { TableColumnConfig } from '@/shared/components/data-table';

export function getBrandProductColumns(
  type: 'brands' | 'products',
  volumeType: VolumeType,
  onDrillDown: (item: BrandWithThemes | ProductWithThemes, sentiment: Sentiment) => void,
) {
  const isProducts = type === 'products';
  const volumeKey = volumeType === 'mention' ? 'mention_volume' : 'post_volume';
  const columnTitle = volumeType === 'mention' ? 'Mention' : 'Post volume';

  return [
    // 1st Column: Index with brand/product icon
    {
      key: '__index__',
      header: isProducts ? '📦' : '🏷️',
      size: '40px',
      tableCellClassName: 'text-center px-6',
      cell: ({ row, table }) => {
        const pageIndex = table.getState().pagination.pageIndex;
        const pageSize = table.getState().pagination.pageSize;
        const rowModel = table.getRowModel();
        const visualIndex = rowModel.rows.findIndex((r) => r.id === row.id);
        const actualIndex = pageIndex * pageSize + visualIndex + 1;
        return (
          <div className="flex items-center justify-center text-sm font-medium">{actualIndex}</div>
        );
      },
    },
    // 2nd Column: Brand/Product name
    {
      key: 'name',
      header: isProducts ? 'Product' : 'Brand',
      size: 'minmax(200px, 1fr)',
      cell: ({ getValue, row }) => {
        const item = row.original;
        const name = getValue() as string;
        const subname = isProducts ? (item as ProductWithThemes).subname : undefined;

        return (
          <div className="font-medium">
            {name}
            {subname && <span className="ml-2 text-sm text-gray-500">({subname})</span>}
          </div>
        );
      },
    },
    // 3rd Column: Volume
    {
      key: volumeKey,
      header: columnTitle,
      size: '128px',
      enableSorting: true,
      tableCellClassName: 'text-right px-6',
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return <div className="text-right">{value?.toLocaleString() || '0'}</div>;
      },
    },
    // 4th Column: Positive themes
    {
      key: 'positive_themes',
      header: (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-[#2a9d90] px-[10px] py-[2px] text-[12px] font-semibold text-[#fbfaf9]">
            Positive
          </span>
        </div>
      ),
      size: '240px',
      enableSorting: true,
      accessorFn: (row: BrandWithThemes | ProductWithThemes) =>
        row.themes?.filter((theme) => theme.sentiment === Sentiment.Positive).length || 0,
      cell: ({ row }) => {
        const item = row.original;

        const positiveThemes =
          item.themes?.filter((theme) => theme.sentiment === Sentiment.Positive) || [];

        return (
          <div data-theme-button>
            <ThemesButton
              themes={positiveThemes.map((theme) => theme.name)}
              onClick={() => onDrillDown(item, Sentiment.Positive)}
            />
          </div>
        );
      },
    },
    // 5th Column: Negative themes
    {
      key: 'negative_themes',
      header: (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-red-600 px-[10px] py-[2px] text-[12px] font-semibold text-[#fbfaf9]">
            Negative
          </span>
        </div>
      ),
      size: '240px',
      enableSorting: true,
      accessorFn: (row: BrandWithThemes | ProductWithThemes) =>
        row.themes?.filter((theme) => theme.sentiment === Sentiment.Negative).length || 0,
      cell: ({ row }) => {
        const item = row.original;

        const negativeThemes =
          item.themes?.filter((theme) => theme.sentiment === Sentiment.Negative) || [];

        return (
          <div data-theme-button>
            <ThemesButton
              themes={negativeThemes.map((theme) => theme.name)}
              onClick={() => onDrillDown(item, Sentiment.Negative)}
            />
          </div>
        );
      },
    },
  ] as TableColumnConfig<BrandWithThemes | ProductWithThemes>[];
}
